import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Percent, CalendarDays, LinkIcon, Eye,
  Send, Pencil, Clock, ClipboardPaste, Paperclip, X, FileText, Image as ImageIcon,
} from "lucide-react";
import ReportDrawer from "@/components/ReportDrawer";
import { format } from "date-fns";

const KPI = ({ label, value, icon: Icon, tone, testId }) => (
  <Card data-testid={testId} className="p-5 bg-[#121215] border-[#27272a] card-hover">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-display text-3xl font-bold mt-2">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </Card>
);

function StatusBadge({ status }) {
  if (status === "submitted") {
    return <Badge className="bg-emerald-500/12 text-emerald-300 border-emerald-500/30" variant="outline">Submitted</Badge>;
  }
  if (status === "missed") {
    return <Badge className="bg-rose-500/12 text-rose-300 border-rose-500/30" variant="outline">Missing</Badge>;
  }
  return <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/30" variant="outline">Pending</Badge>;
}

export default function MyDashboard() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ submission_date: today, report_link: "", notes: "" });
  const [attachments, setAttachments] = useState([]); // uploaded file records
  const [uploading, setUploading] = useState(false);
  const [mine, setMine] = useState([]);
  const [monthly, setMonthly] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waInfo, setWaInfo] = useState(null); // { time, tz, number, optIn, enabled }

  const now = new Date();
  const [year, month] = [now.getFullYear(), now.getMonth() + 1];

  const load = async () => {
    const [m, mo] = await Promise.all([
      api.get("/eod/my"),
      api.get("/dashboard/monthly", { params: { year, month } }),
    ]);
    setMine(m.data); setMonthly(mo.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/managers")]).then(([st, mg]) => {
      const me = mg.data?.[0] || {};
      setWaInfo({
        time: st.data.whatsapp_reminder_time, tz: st.data.timezone,
        enabled: st.data.whatsapp_enabled !== false,
        number: me.whatsapp_number, optIn: me.whatsapp_opt_in !== false,
      });
    }).catch(() => {});
  }, []);

  const submissionByDate = useMemo(() => {
    const map = {};
    for (const r of mine) map[r.submission_date] = r;
    return map;
  }, [mine]);

  const myMonthRow = monthly?.grid?.[0];
  const kpis = myMonthRow ? {
    submitted: myMonthRow.submitted,
    missed: (myMonthRow.working_days || 0) - (myMonthRow.submitted || 0),
    compliance: myMonthRow.compliance_pct,
    working: myMonthRow.working_days,
  } : { submitted: 0, missed: 0, compliance: 0, working: 0 };

  const todayStatus = submissionByDate[today] ? "submitted" : "pending";
  const editingExisting = !!submissionByDate[form.submission_date];

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/eod/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    if (added.length) {
      setAttachments((prev) => [...prev, ...added]);
      toast.success(`${added.length} file(s) attached`);
    }
    setUploading(false);
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.report_link.trim()) { toast.error("Report link is required"); return; }
    setBusy(true);
    try {
      await api.post("/eod/submit", { ...form, file_ids: attachments.map((a) => a.id) });
      toast.success(editingExisting ? "EOD updated" : "EOD submitted");
      setForm({ ...form, notes: "" });
      setAttachments([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const preview = async (id) => {
    const { data } = await api.get(`/reports/${id}`);
    setDrawer(data);
  };

  const prefillForDate = (d) => {
    const existing = submissionByDate[d];
    setForm({
      submission_date: d,
      report_link: existing?.attachments?.[0]?.url || "",
      notes: existing?.body || "",
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">My EOD</div>
        <h1 className="font-display text-3xl font-bold">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <div className="text-sm text-zinc-400 mt-1">
          Submit your EOD report for today or catch up on any missed dates.
        </div>
        {waInfo && waInfo.enabled && (
          <div data-testid="my-whatsapp-info" className="text-xs mt-2 text-zinc-500">
            {waInfo.number && waInfo.optIn
              ? <>📱 If today's EOD isn't in by <span className="text-zinc-300">{waInfo.time}</span> ({waInfo.tz}), you'll get a WhatsApp reminder on {waInfo.number}.</>
              : !waInfo.number
                ? <>📱 No WhatsApp number on file — ask your admin to add one to get daily reminders.</>
                : <>📱 Daily WhatsApp reminders are turned off for you.</>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Today's status" value={<StatusBadge status={todayStatus} />} icon={CalendarDays}
          tone="bg-indigo-500/15 text-indigo-400" testId="my-kpi-today" />
        <KPI label={`Submitted (${format(now, "MMM")})`} value={kpis.submitted} icon={CheckCircle2}
          tone="bg-emerald-500/15 text-emerald-400" testId="my-kpi-submitted" />
        <KPI label="Missing this month" value={kpis.missed} icon={XCircle}
          tone="bg-rose-500/15 text-rose-400" testId="my-kpi-missed" />
        <KPI label="My compliance" value={`${kpis.compliance}%`} icon={Percent}
          tone="bg-amber-500/15 text-amber-400" testId="my-kpi-compliance" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <Card className="lg:col-span-3 p-6 bg-[#121215] border-[#27272a]">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-display text-lg font-semibold">
              {editingExisting ? "Update EOD" : "Submit today's EOD"}
            </h2>
            {editingExisting && (
              <Badge className="bg-amber-500/12 text-amber-300 border-amber-500/30" variant="outline">
                <Pencil className="w-3 h-3 mr-1" /> Re-submitting
              </Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500 mb-4">
            Paste your report link (Google Doc, Sheet, Notion, PDF etc.). Optional notes get summarized by AI.
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">EOD date</Label>
                <Input type="date" value={form.submission_date}
                  onChange={(e) => setForm({ ...form, submission_date: e.target.value })}
                  data-testid="my-eod-date"
                  className="mt-1 bg-[#0f0f12] border-[#27272a]" />
              </div>
              <div className="flex items-end">
                <div className="text-xs text-zinc-500">
                  {editingExisting
                    ? "You already submitted for this date. This will update it."
                    : "New submission for this date."}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Report link</Label>
              <div className="mt-1 flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input value={form.report_link} onChange={(e) => setForm({ ...form, report_link: e.target.value })}
                    data-testid="my-eod-link"
                    placeholder="https://docs.google.com/document/…"
                    className="pl-9 bg-[#0f0f12] border-[#27272a]" required />
                </div>
                <Button type="button" variant="outline" data-testid="my-eod-paste"
                  className="bg-[#0f0f12] border-[#27272a] hover:bg-[#18181b] whitespace-nowrap"
                  onClick={async () => {
                    try {
                      const txt = (await navigator.clipboard.readText() || "").trim();
                      if (!txt) { toast.error("Clipboard is empty"); return; }
                      setForm((f) => ({ ...f, report_link: txt }));
                      toast.success("Link pasted from clipboard");
                    } catch {
                      toast.error("Clipboard access blocked — paste manually");
                    }
                  }}>
                  <ClipboardPaste className="w-4 h-4 mr-1.5" /> Paste
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Tip: Copy your EOD doc URL and click Paste to fill it instantly.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Notes (optional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="my-eod-notes"
                placeholder="Brief summary of today's work…"
                className="mt-1 bg-[#0f0f12] border-[#27272a]" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                Attachments <span className="normal-case text-zinc-500">· PDF or image · max 10 MB each</span>
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 h-10 rounded-md border border-[#27272a] bg-[#0f0f12] hover:bg-[#18181b] text-sm text-zinc-200">
                  <Paperclip className="w-4 h-4 text-zinc-400" />
                  <span>{uploading ? "Uploading…" : "Attach file"}</span>
                  <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
                    multiple
                    data-testid="my-eod-file-input"
                    disabled={uploading}
                    onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
                    className="hidden" />
                </label>
                <span className="text-xs text-zinc-500">{attachments.length} attached</span>
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((a) => {
                    const isImg = (a.content_type || "").startsWith("image/");
                    const Icon = isImg ? ImageIcon : FileText;
                    return (
                      <div key={a.id} data-testid={`my-eod-attachment-${a.id}`}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-[#0f0f12] border border-[#27272a]">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${isImg ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-300" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-100 truncate">{a.original_filename}</div>
                          <div className="text-[11px] text-zinc-500 font-mono">
                            {a.content_type} · {Math.max(1, Math.round((a.size || 0) / 1024))} KB
                          </div>
                        </div>
                        <button type="button" onClick={() => removeAttachment(a.id)}
                          data-testid={`my-eod-attachment-remove-${a.id}`}
                          className="text-zinc-500 hover:text-rose-300 p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={busy} data-testid="my-eod-submit"
                className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Send className="w-4 h-4 mr-2" /> {editingExisting ? "Update EOD" : "Submit EOD"}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="lg:col-span-2 p-6 bg-[#121215] border-[#27272a]">
          <h2 className="font-display text-lg font-semibold mb-1">This month at a glance</h2>
          <div className="text-xs text-zinc-500 mb-4">
            {format(now, "MMMM yyyy")} · {kpis.submitted}/{kpis.working} submitted
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {(monthly?.grid?.[0]?.cells || []).map((c) => (
              <button key={c.date} onClick={() => prefillForDate(c.date)}
                data-testid={`my-day-${c.date}`}
                title={`${c.date} · ${c.status}`}
                className={`hm-cell hm-${c.status}`} />
            ))}
          </div>
          <div className="mt-4 text-xs text-zinc-500 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><span className="hm-cell hm-submitted !w-3 !h-3" /> Submitted</span>
            <span className="inline-flex items-center gap-1"><span className="hm-cell hm-missed !w-3 !h-3" /> Missed</span>
            <span className="inline-flex items-center gap-1"><span className="hm-cell hm-holiday !w-3 !h-3" /> Non-working</span>
            <span className="inline-flex items-center gap-1"><span className="hm-cell hm-pending !w-3 !h-3" /> Pending</span>
          </div>
        </Card>
      </div>

      <Card className="bg-[#121215] border-[#27272a]">
        <div className="p-5 border-b border-[#27272a]">
          <h2 className="font-display text-lg font-semibold">My submission history</h2>
          <div className="text-xs text-zinc-500">Latest submissions first. Click Preview to see the AI summary.</div>
        </div>
        <div className="divide-y divide-[#1f1f23]">
          {mine.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No submissions yet. Submit your first EOD above.</div>
          )}
          {mine.map((r) => (
            <div key={r.id} data-testid={`my-history-${r.id}`}
              className="p-4 flex items-center gap-4 hover:bg-[#18181b]">
              <div className="w-16 shrink-0">
                <div className="text-xs uppercase tracking-wider text-zinc-500">
                  {format(new Date(r.submission_date), "MMM")}
                </div>
                <div className="font-display text-xl font-semibold">
                  {format(new Date(r.submission_date), "d")}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <a href={r.attachments?.[0]?.url} target="_blank" rel="noreferrer"
                  className="text-sm text-indigo-300 hover:text-indigo-200 truncate block">
                  {r.attachments?.[0]?.url || "(no link)"}
                </a>
                <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {format(new Date(r.received_at), "PPp")}
                </div>
              </div>
              <StatusBadge status="submitted" />
              <Button size="sm" variant="ghost" onClick={() => preview(r.id)}
                data-testid={`my-preview-${r.id}`}
                className="text-zinc-300 hover:text-white">
                <Eye className="w-4 h-4 mr-1" /> Preview
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <ReportDrawer open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)} report={drawer} />
    </div>
  );
}
